import React, { useState, useRef } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Container,
  Button,
  Typography,
  Stack,
  Menu,
  MenuItem,
  InputBase,
  IconButton,
  Paper,
  ClickAwayListener,
  Fade,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WavesIcon from '@mui/icons-material/Waves';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PortalIcon from '@mui/icons-material/DoorFront';
import { searchWebsite } from '../utils/search';
import SearchResults from './SearchResults';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

// Modern Color Palette for Water Technology Theme
const navbarColors = {
  primary: '#0288d1',       // Modern Blue
  secondary: '#00acc1',     // Teal accent
  background: '#ffffff',    // Clean white background
  text: {
    primary: '#263238',     // Dark blue-gray
    secondary: '#546e7a'    // Medium blue-gray
  },
  accent: '#00bcd4',        // Bright cyan accent
  border: '#eceff1',        // Very light blue-gray
  hover: '#e1f5fe'          // Light blue hover state
};

// Styled Components with Modern, Clean Design
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: 'none',
  borderBottom: `1px solid ${navbarColors.border}`,
}));

const TopInfoBar = styled(Box)(({ theme }) => ({
  backgroundColor: navbarColors.background,
  borderBottom: `1px solid ${navbarColors.border}`,
  padding: theme.spacing(1.5, 0),
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: navbarColors.text.primary,
  fontFamily: '"Inter", "Segoe UI", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: '0.3px',
  padding: theme.spacing(1.2, 1.5),
  borderRadius: '4px',
  transition: 'all 0.2s ease',
  position: 'relative',
  marginRight: '4px',
  '&:hover': {
    backgroundColor: navbarColors.hover,
    color: navbarColors.primary,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: 0,
    height: '2px',
    backgroundColor: navbarColors.primary,
    transition: 'width 0.2s ease, left 0.2s ease',
  },
  '&:hover::after': {
    width: '80%',
    left: '10%',
  },
  '&.active': {
    color: navbarColors.primary,
    fontWeight: 600,
    '&::after': {
      width: '80%',
      left: '10%',
    }
  }
}));

const NavLink = styled(Link)(({ theme }) => ({
  color: 'white',
  textDecoration: 'none',
  fontFamily: '"Inter", "Segoe UI", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.2)',
    transform: 'translateY(-2px)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -4,
    left: '50%',
    width: 0,
    height: '2px',
    backgroundColor: 'white',
    transition: 'all 0.3s ease',
  },
  '&:hover::after': {
    left: '10%',
    width: '80%',
  },
}));

const pages = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  {
    name: 'Solutions',
    items: [
      { name: 'Industrial ETP', path: '/solutions/industrial-etp' },
      { name: 'ZLD', path: '/solutions/zld' },
      { name: 'STP', path: '/solutions/stp' },
      { name: 'Domestic RO', path: '/solutions/domestic-ro' },
      { name: 'Treatment', path: '/solutions/ro-treatment' },
    ],
  },
  {
    name: 'Patent Technologies',
    path: '/technologies/patent-technologies',
    items: [
      { name: 'REWOLUTE', path: '/technologies/patent-technologies#rewolute' },
      { name: 'BRINEX', path: '/technologies/patent-technologies#brinex' },
      { name: 'CARE', path: '/technologies/patent-technologies#care' },
      { name: 'Contaminants Removal', path: '/technologies/patent-technologies#contaminants' },
    ],
  },
  { name: 'Industries We Serve', path: '/solutions/industries-we-serve' },
  // { name: 'Projects', path: '/projects' },
  {
    name: 'Products',
    items: [
      { name: 'Waste Water Treatment System', path: '/products/waste-water-treatment' },
      { name: 'Equipment & Systems', path: '/products/equipment-systems' },
      { name: 'Accessories', path: '/products/accessories' },
      { name: 'Specialty Chemicals', path: '/products/specialty-chemicals' },
    ],
  },

  { name: 'Blog & Events', path: '/blog-events' },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [anchorEls, setAnchorEls] = useState({});
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const searchInputRef = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();

  const languages = [
    { code: 'en', name: 'EN', flag: '🇺🇸' },
    { code: 'es', name: 'ES', flag: '🇪🇸' },
    { code: 'fr', name: 'FR', flag: '🇫🇷' }
  ];

  const handleLanguageOpen = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    handleLanguageClose();
  };

  const handleOpenMenu = (event, pageName) => {
    setAnchorEls({
      ...anchorEls,
      [pageName]: event.currentTarget
    });
  };

  const handleCloseMenu = (pageName) => {
    setAnchorEls({
      ...anchorEls,
      [pageName]: null
    });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      const results = searchWebsite(searchQuery);
      setSearchResults(results);
      setShowResults(true);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    if (value.trim()) {
      const results = searchWebsite(value);
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const toggleExpandItem = (pageName) => {
    setExpandedItems(prev => ({
      ...prev,
      [pageName]: !prev[pageName]
    }));
  };

  const handleMobileNavigation = (path, isExternal) => {
    if (isExternal) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    handleMobileMenuClose();
  };

  return (
    <StyledAppBar position="sticky">
      {/* Top Information Bar */}
      <TopInfoBar>
        <Container maxWidth="xl">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            {/* Logo Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <img
                  src="/images/logo.png"
                  alt="WTT Logo"
                  style={{ 
                    height: '50px', 
                    borderRadius: '4px',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Link>
              <Box>
                <Typography
                    variant="h6"
                    sx={{
                      color: navbarColors.text.primary,
                      fontWeight: 600,
                      fontSize: '18px',
                      letterSpacing: '0.2px',
                    }}
                  >
                    WTT INTERNATIONAL
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: navbarColors.text.secondary,
                      fontSize: '13px',
                      fontWeight: 400,
                      letterSpacing: '0.1px',
                    }}
                  >
                    Water Loving Technology
                  </Typography>
                </Box>
            </Box>

            {/* Contact and Search Section */}
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={2}
              sx={{ display: { xs: 'none', md: 'flex' } }} // Hide on mobile, show on desktop
            >
              {/* Language Selector */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  backgroundColor: 'rgba(0,0,0,0.03)',
                  borderRadius: '20px',
                  padding: '4px 12px',
                  border: `1px solid ${navbarColors.border}`,
                  cursor: 'pointer'
                }}
                onClick={handleLanguageOpen}
              >
                <LanguageIcon 
                  sx={{ 
                    color: navbarColors.text.secondary, 
                    fontSize: 20 
                  }} 
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: navbarColors.text.secondary,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {languages.find(l => l.code === language)?.name}
                </Typography>
              </Box>
              
              {/* Portal Dropdown */}
              <Box 
                sx={{ 
                  position: 'relative',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  background: 'linear-gradient(135deg, #0277bd 0%, #0288d1 100%)',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  height: '34px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 6px rgba(2, 119, 189, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 10px rgba(2, 119, 189, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 4px rgba(2, 119, 189, 0.2)'
                  }
                }}
                onClick={(e) => handleOpenMenu(e, 'portalMenu')}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    mr: 0.5,
                    fontSize: '15px',
                    transition: 'transform 0.2s ease',
                    '.MuiBox-root:hover &': {
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <i className="fas fa-user-shield"></i>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '14px',
                    letterSpacing: '0.2px'
                  }}
                >
                  Portal
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    ml: 0.5,
                    transition: 'transform 0.3s ease',
                    '.MuiBox-root:hover &': {
                      transform: 'translateY(2px)'
                    }
                  }}
                >
                  <i className="fas fa-chevron-down" style={{ fontSize: '11px' }}></i>
                </Box>
              </Box>
              
              <Menu
                anchorEl={anchorEls['portalMenu']}
                open={Boolean(anchorEls['portalMenu'])}
                onClose={() => handleCloseMenu('portalMenu')}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                TransitionProps={{ timeout: 200 }}
                PaperProps={{
                  elevation: 8,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.15))',
                    mt: 1.5,
                    borderRadius: '16px',
                    minWidth: 260,
                    border: '1px solid rgba(0,0,0,0.05)',
                    p: 1,
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      left: 14,
                      width: 12,
                      height: 12,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                      borderTop: '1px solid rgba(0,0,0,0.05)',
                      borderLeft: '1px solid rgba(0,0,0,0.05)',
                    },
                  },
                }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, mb: 0.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: navbarColors.text.secondary, 
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      opacity: 0.8
                    }}
                  >
                    Select Portal
                  </Typography>
                </Box>
                {/* Customer Portal */}
                <MenuItem
                  component={Link}
                  to="/customer-portal"
                  onClick={() => handleCloseMenu('portalMenu')}
                  sx={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    margin: '4px 8px',
                    color: navbarColors.text.primary,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${navbarColors.primary}08`,
                      transform: 'translateX(3px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${navbarColors.primary}15 0%, ${navbarColors.primary}30 100%)`,
                      borderRadius: '12px',
                      padding: '10px',
                      width: 42,
                      height: 42,
                      boxShadow: `0 4px 12px ${navbarColors.primary}15`,
                      transition: 'all 0.3s ease',
                      '.MuiMenuItem-root:hover &': {
                        transform: 'scale(1.05) rotate(-5deg)',
                        boxShadow: `0 6px 16px ${navbarColors.primary}25`,
                      }
                    }}>
                      <BusinessIcon fontSize="small" sx={{ color: navbarColors.primary }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '15px', mb: 0.3 }}>Customer Portal</Typography>
                      <Typography variant="caption" sx={{ color: navbarColors.text.secondary, fontSize: '12px' }}>Business account access</Typography>
                    </Box>
                    <Box 
                      component="span" 
                      sx={{ 
                        color: navbarColors.text.secondary,
                        opacity: 0,
                        transition: 'all 0.2s ease',
                        transform: 'translateX(-5px)',
                        '.MuiMenuItem-root:hover &': {
                          opacity: 0.5,
                          transform: 'translateX(0)'
                        }
                      }}
                    >
                      <i className="fas fa-chevron-right" style={{ fontSize: '12px' }}></i>
                    </Box>
                  </Box>
                </MenuItem>
                
                <Divider sx={{ my: 1.5, mx: 2, opacity: 0.6 }} />
                
                {/* Employee Portal */}
                <MenuItem
                  onClick={() => {
                    window.open('https://erp.wttindia.com', '_blank');
                    handleCloseMenu('portalMenu');
                  }}
                  sx={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    margin: '4px 8px',
                    color: navbarColors.text.primary,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${navbarColors.secondary}08`,
                      transform: 'translateX(3px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${navbarColors.secondary}15 0%, ${navbarColors.secondary}30 100%)`,
                      borderRadius: '12px',
                      padding: '10px',
                      width: 42,
                      height: 42,
                      boxShadow: `0 4px 12px ${navbarColors.secondary}15`,
                      transition: 'all 0.3s ease',
                      '.MuiMenuItem-root:hover &': {
                        transform: 'scale(1.05) rotate(-5deg)',
                        boxShadow: `0 6px 16px ${navbarColors.secondary}25`,
                      }
                    }}>
                      <PersonIcon fontSize="small" sx={{ color: navbarColors.secondary }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '15px', mb: 0.3 }}>Employee Portal</Typography>
                      <Typography variant="caption" sx={{ color: navbarColors.text.secondary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Staff login system
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: navbarColors.text.secondary,
                        transition: 'all 0.2s ease',
                        '.MuiMenuItem-root:hover &': {
                          color: navbarColors.secondary,
                        }
                      }}
                    >
                      <i className="fas fa-external-link-alt" style={{ fontSize: '12px' }}></i>
                    </Box>
                  </Box>
                </MenuItem>
                
                {/* Footer */}
                <Box sx={{ px: 2, py: 1.5, mt: 0.5 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: navbarColors.text.secondary, 
                      fontSize: '11px',
                      opacity: 0.7,
                      display: 'block',
                      textAlign: 'center'
                    }}
                  >
                    Secure access to WTT portals
                  </Typography>
                </Box>
              </Menu>
              
              <Menu
                anchorEl={languageAnchorEl}
                open={Boolean(languageAnchorEl)}
                onClose={handleLanguageClose}
                TransitionComponent={Fade}
                PaperProps={{
                  sx: {
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                    mt: 1,
                    minWidth: 180,
                    background: 'white',
                    border: `1px solid ${navbarColors.border}`,
                    backdropFilter: 'blur(8px)',
                  }
                }}
              >
                {languages.map((lang) => (
                  <MenuItem 
                    key={lang.code} 
                    onClick={() => handleLanguageChange(lang.code)}
                    selected={language === lang.code}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      color: navbarColors.text.primary,
                      fontWeight: language === lang.code ? 600 : 500,
                      '&:hover': {
                        backgroundColor: `${navbarColors.primary}10`,
                        color: navbarColors.primary,
                      },
                      '&.Mui-selected': {
                        backgroundColor: `${navbarColors.primary}15`,
                        color: navbarColors.primary,
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: '1.2rem', mr: 1 }}>
                      {lang.flag}
                    </Typography>
                    {lang.name}
                  </MenuItem>
                ))}
              </Menu>

              {/* Existing contact and search section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  component="a" 
                  href="tel:04216531236" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: navbarColors.text.secondary,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: navbarColors.primary,
                    }
                  }}
                >
                  <PhoneIcon sx={{ color: navbarColors.primary, fontSize: 20 }} />
                  <Typography variant="body2">0421 653 1236</Typography>
                </Box>
                <Box 
                  component="a" 
                  href="mailto:enquiry@wttint.com" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: navbarColors.text.secondary,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: navbarColors.primary,
                    }
                  }}
                >
                  <EmailIcon sx={{ color: navbarColors.primary, fontSize: 20 }} />
                  <Typography variant="body2">enquiry@wttint.com</Typography>
                </Box>
              </Box>

              {/* Search */}
              <ClickAwayListener onClickAway={handleCloseResults}>
                <Box sx={{ position: 'relative' }}>
                  <Paper
                    component="form"
                    onSubmit={handleSearch}
                    sx={{
                      width: { xs: 180, sm: 240 },
                      borderRadius: '32px',
                      border: `1px solid ${navbarColors.primary}20`,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        boxShadow: `0 2px 8px ${navbarColors.primary}10`,
                        borderColor: navbarColors.primary,
                      }
                    }}
                  >
                    <InputBase
                      sx={{
                        ml: 2,
                        flex: 1,
                        fontSize: '14px',
                        color: navbarColors.text.primary,
                      }}
                      placeholder="Search..."
                      inputProps={{ 'aria-label': 'search' }}
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <IconButton 
                      type="submit" 
                      sx={{ 
                        p: '8px',
                        color: navbarColors.primary,
                      }}
                      aria-label="search"
                    >
                      <SearchIcon />
                    </IconButton>
                  </Paper>
                  {showResults && (
                    <SearchResults 
                      results={searchResults} 
                      onClose={handleCloseResults} 
                    />
                  )}
                </Box>
              </ClickAwayListener>
            </Stack>
          </Stack>
        </Container>
      </TopInfoBar>

      {/* Navigation Menu - Desktop */}
      <Toolbar 
        sx={{ 
          minHeight: '50px !important',
          px: { xs: 1, md: 4 },
          backgroundColor: navbarColors.background,
          borderBottom: `1px solid ${navbarColors.primary}10`,
          display: { xs: 'none', md: 'flex' }, // Hide on mobile
        }}
      >
        <Container maxWidth="xl">
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="center" 
            alignItems="center"
            sx={{
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            {pages.map((page) => (
              page.items ? (
                <Box 
                  key={page.name}
                  onMouseEnter={(e) => handleOpenMenu(e, page.name)}
                  onMouseLeave={() => handleCloseMenu(page.name)}
                >
                  <NavButton
                    startIcon={page.icon}
                    endIcon={<KeyboardArrowDownIcon />}
                  >
                    {page.name}
                  </NavButton>
                  <Menu
                    anchorEl={anchorEls[page.name]}
                    open={Boolean(anchorEls[page.name])}
                    onClose={() => handleCloseMenu(page.name)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    sx={{
                      '& .MuiPaper-root': {
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        mt: 1,
                        minWidth: 250,
                      }
                    }}
                  >
                    {page.items.map((item) => (
                      <MenuItem 
                        key={item.name} 
                        onClick={() => {
                          if (item.external) {
                            window.open(item.path, '_blank');
                          } else {
                            navigate(item.path);
                          }
                          handleCloseMenu(page.name);
                        }}
                        sx={{
                          color: navbarColors.text.primary,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: `${navbarColors.primary}10`,
                            color: navbarColors.primary,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.icon && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {item.icon}
                            </Box>
                          )}
                          {item.name}
                          {item.external && (
                            <Box component="span" sx={{ fontSize: '0.75rem' }}>
                              <i className="fas fa-external-link-alt"></i>
                            </Box>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ) : (
                <NavButton
                  key={page.name}
                  component={Link}
                  to={page.path}
                >
                  {page.name}
                </NavButton>
              )
            ))}
          </Stack>
        </Container>
      </Toolbar>
      
      {/* Mobile Navigation Bar */}
      <Toolbar 
        sx={{ 
          minHeight: '50px !important',
          px: { xs: 2 },
          backgroundColor: navbarColors.background,
          borderBottom: `1px solid ${navbarColors.primary}10`,
          display: { xs: 'flex', md: 'none' }, // Show only on mobile
          justifyContent: 'flex-start', // Align items to the left
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleMobileMenu}
          sx={{ color: navbarColors.primary }}
        >
          <MenuIcon />
        </IconButton>
        
        {/* Menu text removed as requested */}
      </Toolbar>
      
      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '85%',
            maxWidth: '320px',
            boxSizing: 'border-box',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/images/logo.png"
              alt="WTT Logo"
              style={{ height: '40px', marginRight: '12px' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, color: navbarColors.text.primary }}>
              WTT
            </Typography>
          </Box>
          <IconButton onClick={handleMobileMenuClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider />
        
        <List sx={{ width: '100%' }}>
          {pages.map((page) => (
            page.items ? (
              <React.Fragment key={page.name}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => toggleExpandItem(page.name)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      {page.icon}
                    </Box>
                    <ListItemText 
                      primary={page.name} 
                      sx={{ 
                        '& .MuiTypography-root': { 
                          fontWeight: 600,
                          color: navbarColors.text.primary
                        } 
                      }} 
                    />
                    {expandedItems[page.name] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </ListItemButton>
                </ListItem>
                
                <Collapse in={expandedItems[page.name]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {page.items.map((item) => (
                      <ListItemButton 
                        key={item.name} 
                        sx={{ pl: 4 }}
                        onClick={() => handleMobileNavigation(item.path, item.external)}
                      >
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.icon && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {item.icon}
                                </Box>
                              )}
                              {item.name}
                              {item.external && (
                                <Box component="span" sx={{ fontSize: '0.75rem' }}>
                                  <i className="fas fa-external-link-alt"></i>
                                </Box>
                              )}
                            </Box>
                          } 
                          sx={{ 
                            '& .MuiTypography-root': { 
                              fontSize: '0.9rem',
                              color: navbarColors.text.secondary
                            } 
                          }} 
                        />
                      </ListItemButton>
                    ))}
                  
                  </List>
                </Collapse>
              </React.Fragment>
            ) : (
              <ListItem key={page.name} disablePadding>
                <ListItemButton onClick={() => handleMobileNavigation(page.path)}>
                  <ListItemText 
                    primary={page.name} 
                    sx={{ 
                      '& .MuiTypography-root': { 
                        fontWeight: 600,
                        color: navbarColors.text.primary
                      } 
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            )
          ))}
        </List>
        
        <Divider />
        
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: navbarColors.text.secondary }}>
            Contact Us
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PhoneIcon sx={{ fontSize: 18, mr: 1, color: navbarColors.primary }} />
            <Typography variant="body2">0421 653 1236</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ fontSize: 18, mr: 1, color: navbarColors.primary }} />
            <Typography variant="body2">enquiry@wttint.com</Typography>
          </Box>
        </Box>
      </Drawer>
    </StyledAppBar>
  );
};

export default Navbar;
